import React, { useState, useRef, useEffect } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router'
import { LockToolTipIcon } from './upgrade-lock-icon-layout'

const CustomDropdown = ({
  label,
  data,
  dataList,
  onFieldChange,
  field,
  startingLength = 4,
  sample = '',
  needUpgrade = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [filteredDataList, setFilteredDataList] = useState(dataList)
  const [highlightedIndex, setHighlightedIndex] = useState(-1) // Track the highlighted option index
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  const dropdownOptionsRef = useRef(null) // Reference to the dropdown options container

  const handleInputChange = (e) => {
    const { value } = e.target
    onFieldChange(e, field)

    const filtered = dataList.filter((d) =>
      d.toLowerCase().includes(value.toLowerCase())
    )
    setFilteredDataList(filtered)
    setHighlightedIndex(-1) // Reset highlighted index on input change

    // Open dropdown if there are matching options
    if (filtered.length > 0 && value.length >= startingLength) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }

  const handleDataSelect = (data) => {
    onFieldChange({ target: { value: data } }, field)
    setIsOpen(false)
    setHighlightedIndex(-1) // Reset highlighted index on selection
  }

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false)
      setHighlightedIndex(-1) // Reset highlighted index on click outside
    }
  }

  const handleKeyDown = (e) => {
    if (isOpen) {
      if (e.key === 'ArrowDown') {
        setHighlightedIndex((prevIndex) =>
          prevIndex < filteredDataList.length - 1 ? prevIndex + 1 : prevIndex
        )
        e.preventDefault() // Prevent the cursor from moving down in the input
      } else if (e.key === 'ArrowUp') {
        setHighlightedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : prevIndex
        )
        e.preventDefault() // Prevent the cursor from moving up in the input
      } else if (e.key === 'Enter' && highlightedIndex >= 0) {
        handleDataSelect(filteredDataList[highlightedIndex])
        e.preventDefault() // Prevent form submission if inside a form
      }
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (dataList) {
      const filtered = dataList.filter((d) =>
        d.toLowerCase().includes(data.toLowerCase())
      )
      setFilteredDataList(filtered)

      // Open dropdown if there are matching options
      if (filtered.length > 0 && data.length >= startingLength) {
        setIsOpen(true)
      } else {
        setIsOpen(false)
      }
    }
  }, [dataList])

  useEffect(() => {
    // Scroll the highlighted option into view if it's available
    if (dropdownOptionsRef.current && highlightedIndex >= 0) {
      const highlightedOption =
        dropdownOptionsRef.current.children[highlightedIndex]
      if (highlightedOption) {
        highlightedOption.scrollIntoView({
          block: 'nearest', // Align the option within the dropdown
          behavior: 'smooth', // Optional: smooth scroll animation
        })
      }
    }
  }, [highlightedIndex])

  const styles = {
    container: { position: 'relative', marginBottom: '15px' },
    label: { display: 'block', marginBottom: '5px', fontWeight: 'bold' },
    input: {
      width: '100%',
      padding: '8px',
      borderRadius: '8px',
      border: '1px solid #ccc',
    },
    dropdown: {
      position: 'fixed', // Change to fixed to overlay the modal
      top: inputRef.current
        ? `${
            inputRef.current.getBoundingClientRect().bottom + window.scrollY
          }px`
        : '100%', // Position it below the input field
      left: inputRef.current
        ? `${inputRef.current.getBoundingClientRect().left}px`
        : '0', // Align with the input field
      width: inputRef.current ? `${inputRef.current.offsetWidth}px` : '224px', // Match the dropdown width with input
      backgroundColor: 'white',
      border: '1px solid #ccc',
      zIndex: 1000, // Ensure it overlays on top
      maxHeight: '300px', // Optional: set a max height for the dropdown
      overflowY: 'auto', // Allow scrolling if content exceeds max height
    },
    option: (isHighlighted) => ({
      padding: '10px',
      cursor: 'pointer',
      borderBottom: '1px solid #eee',
      backgroundColor: isHighlighted ? '#f0f0f0' : 'white', // Highlight style
    }),
    noOptions: { padding: '10px', color: '#999' },
  }

  return (
    <div style={styles.container} ref={dropdownRef}>
      <div className="flex flex-row space-x-1 w-full">
        <label
          className={cn('whitespace-nowrap', needUpgrade && 'opacity-50')}
          style={styles.label}
        >
          {label}:
        </label>
        <LockToolTipIcon show={needUpgrade} />
      </div>
      <input
        ref={inputRef}
        type="text"
        style={styles.input}
        placeholder={
          sample ? sample : `Type at least ${startingLength} letters.`
        }
        disabled={needUpgrade}
        value={data}
        onChange={handleInputChange}
        onFocus={() => filteredDataList.length > 0 && setIsOpen(true)}
        onKeyDown={handleKeyDown} // Add keydown event
      />
      {isOpen && (
        <div style={styles.dropdown} ref={dropdownOptionsRef}>
          {filteredDataList.length > 0 ? (
            filteredDataList.map((d, index) => (
              <div
                key={d}
                style={styles.option(index === highlightedIndex)} // Highlight the selected option
                onMouseDown={() => handleDataSelect(d)}
                onMouseEnter={() => setHighlightedIndex(index)} // Set highlightedIndex on hover
              >
                {d}
              </div>
            ))
          ) : (
            <div style={styles.noOptions}>No options available</div>
          )}
        </div>
      )}
    </div>
  )
}

export default CustomDropdown
